// What Isolate in 3D keeps of an object it isolates only a part of — a room's
// bench, door or partition, a drawn wall's door (docs/claude/rendering.md,
// *Isolate and hide in 3D*). DOM-free: the targets in, the parts kept out.
import type {
  SecPart,
  SecSrc,
} from './section';

/** A part's key among what is kept. */
export const partKey = (p: Pick<SecPart, 'kind' | 'id'>): string => `${p.kind}:${p.id}`;

/** The objects isolated only in part, each with the keys of the parts it keeps:
 *  the targets that name a part, unless the same object is a target whole. */
export function isolatedParts(targets: readonly SecSrc[]): Map<string, Set<string>> {
  const whole = new Set(targets.filter((t) => !t.part).map((t) => t.id));
  const out = new Map<string, Set<string>>();
  for (const t of targets) {
    if (!t.part || whole.has(t.id)) continue;
    const keys = out.get(t.id) ?? new Set<string>();
    keys.add(partKey(t.part));
    out.set(t.id, keys);
  }
  return out;
}

/** Whether a mesh of an object isolated in part still shows: it is a kept part,
 *  or a support under a kept bench; the object's own meshes (a room's walls,
 *  floor and ceiling) and its other parts do not. */
export function keepsPart(keys: ReadonlySet<string>, part: SecPart | undefined): boolean {
  if (!part) return false;
  if (keys.has(partKey(part))) return true;
  return part.kind === 'support' && !!part.bench && keys.has(partKey({ kind: 'bench', id: part.bench }));
}
